import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { BookOpen, Activity, Users, MessageSquare, Search, TrendingUp, Calendar, Apple, ChefHat, Heart, ChevronDown, ChevronUp, Headphones, Smile, AlertCircle, Phone, Wind, X, Bookmark, Flame, FileText, Award, Moon, Cloud, FlaskConical, Syringe, Pill, CalendarDays, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { exportCareTeamSummaryPDF } from '../utils/pdfExport';
import { NewsBanner } from '../components/NewsBell';
import { todayLocal, formatLocalDate, toLocalDateString } from '../utils/dateUtils';

export const Dashboard = () => {
  const { user, API, token } = useAuth();
  const navigate = useNavigate();
  const [recentSymptoms, setRecentSymptoms] = useState([]);
  const [todayCheckIn, setTodayCheckIn] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [journalEntry, setJournalEntry] = useState('');
  const [showJournal, setShowJournal] = useState(false);
  const [showTechnique, setShowTechnique] = useState(false);
  const [currentTechnique, setCurrentTechnique] = useState(null);
  const [techniqueIndex, setTechniqueIndex] = useState(0);
  const [showWeeklyReflection, setShowWeeklyReflection] = useState(false);
  const [weeklyReflection, setWeeklyReflection] = useState('');
  const [checkInHistory, setCheckInHistory] = useState([]);
  const [showInsights, setShowInsights] = useState(false);
  const [showCrisisModal, setShowCrisisModal] = useState(false);
  const [bookmarkedTools, setBookmarkedTools] = useState([]);
  const [showFlarePrompt, setShowFlarePrompt] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [showQuickFlare, setShowQuickFlare] = useState(false);
  const [quickFlareData, setQuickFlareData] = useState({
    severity: 7,
    symptoms: [],
    behaviors: [],
    what_helped: '',
    notes: ''
  });
  const [quickFlareSaving, setQuickFlareSaving] = useState(false);
  const [careTeamLoading, setCareTeamLoading] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  const groundingMessages = [
    "Flares are states, not erasures.",
    "You are navigating the impossible with grace.",
    "Healing isn't linear, and neither is progress.",
    "Your child is more than their symptoms.",
    "Small steps forward still move you forward.",
    "This moment doesn't define the whole journey.",
    "You're doing better than you think you are.",
    "Rest is productive. Pausing is strategic.",
    "Every day you show up is a victory.",
    "Non-linear days are expected, not failures.",
    "You don't have to have all the answers today.",
    "Progress hides in places we're not always looking.",
    "Your love is their anchor, even in the storm.",
    "You are the expert on your child.",
    "Difficult moments don't erase all the good ones.",
    "It's okay to grieve what you expected while loving what is.",
    "You are not alone in this fight.",
    "Some days surviving is thriving.",
    "Your child feels your love, even when they can't show it.",
    "Advocacy is an act of love.",
    "You are stronger than you know.",
    "This chapter is hard, but it's not the whole story.",
    "Give yourself the grace you give your child.",
    "You are learning, growing, and becoming.",
    "Tomorrow is a fresh start.",
    "Your presence matters more than perfection.",
    "One breath at a time. One moment at a time.",
    "You are doing an incredible job under impossible circumstances.",
    "The hard days make you appreciate the good ones.",
    "Your child chose the right parent to fight for them."
  ];

  const commonSymptoms = ['OCD thoughts', 'Anxiety', 'Rage', 'Tics', 'Sleep issues', 'Sensory sensitivity'];
  const commonBehaviors = ['Meltdowns', 'Clinginess', 'Aggression', 'Withdrawal', 'Crying spells'];

  const groundingTechniques = {
    calm: [
      {
        title: "Gentle Gratitude",
        description: "Take a moment to notice three things going well today, no matter how small.",
        steps: [
          "Close your eyes and take a slow breath",
          "Name one thing you're grateful for today",
          "Name a second thing - perhaps something your child did",
          "Name a third thing - maybe something about yourself"
        ]
      },
      {
        title: "Savoring the Moment",
        description: "When things are calm, practice being fully present.",
        steps: [
          "Notice what calm feels like in your body",
          "Take three slow, deep breaths",
          "Look around and find something beautiful or comforting",
          "Give yourself permission to enjoy this moment",
          "Remember: you can return to this feeling"
        ]
      },
      {
        title: "Future Self Letter",
        description: "On calm days, leave a message for yourself when things get hard.",
        steps: [
          "Think about what's helping you feel calm today",
          "Write a short note to your future self",
          "Remind yourself that calm days do come",
          "Keep this note for tough days ahead"
        ]
      }
    ],
    wobbly: [
      {
        title: "5-4-3-2-1 Grounding",
        description: "This technique helps anchor you when things feel uncertain.",
        steps: [
          "Name 5 things you can see around you",
          "Name 4 things you can touch",
          "Name 3 things you can hear",
          "Name 2 things you can smell",
          "Name 1 thing you can taste"
        ]
      },
      {
        title: "Body Scan Check-in",
        description: "Notice tension and gently release it.",
        steps: [
          "Sit comfortably and close your eyes",
          "Notice your feet - wiggle your toes",
          "Relax your shoulders - roll them back",
          "Soften your jaw - take a deep breath",
          "Place a hand on your heart - you're okay"
        ]
      },
      {
        title: "Wise Friend Perspective",
        description: "Talk to yourself like you'd talk to a friend.",
        steps: [
          "What would you say to a friend feeling this way?",
          "Offer yourself the same compassion",
          "Acknowledge this is hard without judgment",
          "Remind yourself: wobbles are temporary"
        ]
      }
    ],
    intense: [
      {
        title: "Box Breathing",
        description: "A calming breath pattern used by emergency responders to manage stress.",
        steps: [
          "Breathe in slowly for 4 counts",
          "Hold your breath for 4 counts",
          "Breathe out slowly for 4 counts",
          "Hold empty for 4 counts",
          "Repeat 4 times or until you feel calmer"
        ]
      },
      {
        title: "Safe Place Visualization",
        description: "Create a mental sanctuary when overwhelm hits.",
        steps: [
          "Close your eyes and take a deep breath",
          "Picture a place where you feel completely safe",
          "Notice the details - colors, sounds, temperature",
          "Stay here for a few moments",
          "Know you can return to this place anytime"
        ]
      },
      {
        title: "Emergency Self-Care",
        description: "Immediate actions for overwhelming moments.",
        steps: [
          "Get yourself a glass of cold water - drink it slowly",
          "Step outside or open a window for fresh air",
          "Text or call someone safe (even just 'having a hard day')",
          "Lower your expectations for today to absolute basics",
          "Remember: You've survived 100% of your hard days so far"
        ]
      }
    ]
  };

  const shuffleMessage = () => {
    let newIndex;
    do {
      newIndex = Math.floor(Math.random() * groundingMessages.length);
    } while (newIndex === currentMessageIndex && groundingMessages.length > 1);
    setCurrentMessageIndex(newIndex);
  };

  const isSunday = () => {
    return new Date().getDay() === 0;
  };

  // Initialize with a random message on mount
  useEffect(() => {
    setCurrentMessageIndex(Math.floor(Math.random() * groundingMessages.length));
  }, []);

  useEffect(() => {
    fetchDashboardData();
    fetchTodayCheckIn();
    fetchCheckInHistory();
    checkWeeklyReflection();
    fetchBookmarkedTools();
    fetchSubscriptionInfo();
  }, []);

  const fetchSubscriptionInfo = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/subscriptions/my-subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription info:', error);
    }
  };

  const fetchBookmarkedTools = async () => {
    try {
      const response = await axios.get(`${API}/bookmarks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookmarkedTools(response.data);
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
    }
  };

  const fetchTodayCheckIn = async () => {
    try {
      const response = await axios.get(`${API}/check-in/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setTodayCheckIn(response.data);
        setSelectedStatus(response.data.status);
        setJournalEntry(response.data.journal_entry || '');
      }
    } catch (error) {
      console.error('Error fetching check-in:', error);
    }
  };

  const fetchCheckInHistory = async () => {
    try {
      const response = await axios.get(`${API}/check-in/history?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCheckInHistory(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const checkWeeklyReflection = async () => {
    if (!isSunday()) return;
    
    try {
      const response = await axios.get(`${API}/weekly-reflection/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.data) {
        setShowWeeklyReflection(true);
      }
    } catch (error) {
      console.error('Error checking reflection:', error);
    }
  };

  const handleStatusChange = async (status) => {
    setSelectedStatus(status);
    setShowJournal(true); // Show journal for all statuses now
    
    // Rotate through techniques for variety
    const techniques = groundingTechniques[status];
    const newIndex = techniqueIndex % techniques.length;
    setCurrentTechnique(techniques[newIndex]);
    setTechniqueIndex(newIndex + 1);
    setShowTechnique(true);
    
    // Show flare journal prompt for intense days
    if (status === 'intense') {
      setShowFlarePrompt(true);
    }
    
    try {
      const today = todayLocal();
      await axios.post(
        `${API}/check-in`,
        { date: today, status, notes: '', journal_entry: journalEntry },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTodayCheckIn();
      fetchCheckInHistory();
    } catch (error) {
      console.error('Error saving check-in:', error);
    }
  };

  const saveJournalEntry = async () => {
    try {
      const today = todayLocal();
      await axios.post(
        `${API}/check-in`,
        { date: today, status: selectedStatus, notes: '', journal_entry: journalEntry },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Journal entry saved');
      fetchTodayCheckIn();
    } catch (error) {
      toast.error('Failed to save journal entry');
    }
  };

  const saveWeeklyReflection = async () => {
    try {
      const today = new Date();
      const weekStart = new Date(today.setDate(today.getDate() - today.getDay() + 1));
      await axios.post(
        `${API}/weekly-reflection`,
        { week_start: toLocalDateString(weekStart), coping_strategies: weeklyReflection },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Weekly reflection saved');
      setShowWeeklyReflection(false);
      setWeeklyReflection('');
    } catch (error) {
      toast.error('Failed to save reflection');
    }
  };

  const toggleQuickSymptom = (symptom) => {
    setQuickFlareData(prev => ({
      ...prev,
      symptoms: prev.symptoms.includes(symptom)
        ? prev.symptoms.filter(s => s !== symptom)
        : [...prev.symptoms, symptom]
    }));
  };

  const toggleQuickBehavior = (behavior) => {
    setQuickFlareData(prev => ({
      ...prev,
      behaviors: prev.behaviors.includes(behavior)
        ? prev.behaviors.filter(b => b !== behavior)
        : [...prev.behaviors, behavior]
    }));
  };

  const saveQuickFlare = async () => {
    if (quickFlareData.symptoms.length === 0 && quickFlareData.behaviors.length === 0) {
      toast.error('Please select at least one symptom or behavior');
      return;
    }
    
    setQuickFlareSaving(true);
    try {
      const today = todayLocal();
      await axios.post(`${API}/flare-journal`, {
        date: today,
        severity: quickFlareData.severity,
        symptoms: quickFlareData.symptoms,
        behaviors: quickFlareData.behaviors,
        what_helped: quickFlareData.what_helped,
        notes: quickFlareData.notes,
        is_flare_ongoing: true,
        flare_start_date: today
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Flare entry saved');
      setShowQuickFlare(false);
      setQuickFlareData({
        severity: 7,
        symptoms: [],
        behaviors: [],
        what_helped: '',
        notes: ''
      });
    } catch (error) {
      toast.error('Failed to save flare entry');
    } finally {
      setQuickFlareSaving(false);
    }
  };

  const generateCareTeamSummary = async () => {
    setCareTeamLoading(true);
    try {
      const [providersRes, supplementsRes, appointmentsRes] = await Promise.all([
        fetch(`${API}/providers`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/supplements`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/appointments`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (!providersRes.ok || !supplementsRes.ok || !appointmentsRes.ok) {
        throw new Error('Failed to fetch data from one or more APIs');
      }
      
      const providersData = await providersRes.json();
      const supplementsData = await supplementsRes.json();
      const appointmentsData = await appointmentsRes.json();
      
      const providers = providersData.providers || [];
      const supplements = supplementsData.supplements || [];
      const appointments = appointmentsData.appointments || [];
      
      await exportCareTeamSummaryPDF(providers, supplements, appointments, user?.name || 'My Child', token);
      toast.success('Care Team Summary PDF generated!');
    } catch (error) {
      console.error('Error generating care team summary:', error);
      toast.error('Failed to generate summary. Please try again.');
    } finally {
      setCareTeamLoading(false);
    }
  };

  const getMoodStats = () => {
    const moodCounts = checkInHistory.reduce((acc, entry) => {
      acc[entry.status] = (acc[entry.status] || 0) + 1;
      return acc;
    }, {});
    
    const total = checkInHistory.length;
    return {
      calm: total > 0 ? Math.round((moodCounts.calm || 0) / total * 100) : 0,
      wobbly: total > 0 ? Math.round((moodCounts.wobbly || 0) / total * 100) : 0,
      intense: total > 0 ? Math.round((moodCounts.intense || 0) / total * 100) : 0
    };
  };

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/symptoms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const entries = response.data;
      setRecentSymptoms(entries.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const quickActions = [
    {
      icon: Activity,
      title: 'Symptom Tracker',
      description: 'Log today\'s symptoms',
      color: 'text-yellow-600',
      path: '/tracker',
      testId: 'dashboard-tracker-link'
    },
    {
      icon: Apple,
      title: 'Food Tracker',
      description: 'Log meals & reactions',
      color: 'text-green-600',
      path: '/food-tracker',
      testId: 'dashboard-food-link'
    },
    {
      icon: Moon,
      title: 'Sleep Tracker',
      description: 'Track sleep patterns',
      color: 'text-indigo-600',
      path: '/sleep-tracker',
      testId: 'dashboard-sleep-link'
    },
    {
      icon: Cloud,
      title: 'Weather Tracker',
      description: 'Log weather & symptoms',
      color: 'text-sky-600',
      path: '/weather-tracker',
      testId: 'dashboard-weather-link'
    },
    {
      icon: Heart,
      title: 'Cycle Tracker',
      description: 'Track menstrual cycle',
      color: 'text-pink-600',
      path: '/cycle-tracker',
      testId: 'dashboard-cycle-link'
    },
    {
      icon: FlaskConical,
      title: 'Lab Tracker',
      description: 'Track lab results',
      color: 'text-cyan-600',
      path: '/lab-tracker',
      testId: 'dashboard-lab-link'
    },
    {
      icon: Syringe,
      title: 'Vaccine Tracker',
      description: 'Log vaccinations',
      color: 'text-teal-600',
      path: '/vaccines',
      testId: 'dashboard-vaccine-link'
    },
    {
      icon: Pill,
      title: 'Med & Supplements',
      description: 'Track medications',
      color: 'text-purple-600',
      path: '/supplements',
      testId: 'dashboard-supplements-link'
    },
    {
      icon: CalendarDays,
      title: 'Appointments',
      description: 'Manage appointments',
      color: 'text-blue-600',
      path: '/appointments',
      testId: 'dashboard-appointments-link'
    },
    {
      icon: GraduationCap,
      title: 'Teacher Reports',
      description: 'Invite teachers & view check-ins',
      color: 'text-emerald-600',
      path: '/manage-teachers',
      testId: 'dashboard-teachers-link'
    }
  ];

  return (
    <div className="space-y-8" data-testid="dashboard">
      <NewsBanner />
      {/* Welcome Section */}
      <div className="bg-gradient-to-br from-primary/10 to-secondary/20 rounded-xl p-8 md:p-12">
        <div className="flex items-center gap-4 mb-3">
          <img 
            src="/logo.png" 
            alt="Calm Pathways" 
            className="w-20 h-20 object-contain"
          />
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <h1 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D]">
              Welcome back, {user?.name}!
            </h1>
            {subscriptionInfo?.is_founding_member && (
              <div 
                className="flex items-center gap-1.5 bg-gradient-to-r from-amber-500 to-yellow-400 text-white px-3 py-1 rounded-full shadow-md text-sm"
                data-testid="dashboard-founding-badge"
              >
                <Award className="w-4 h-4" />
                <span className="font-semibold">Founding Member</span>
              </div>
            )}
          </div>
        </div>
        <p className="text-lg text-[#57534E]">
          Here's your personalized dashboard with quick access to all your resources.
        </p>
      </div>

      {/* Grounding Section */}
      <Card className="bg-white rounded-xl border-2 border-primary/20 shadow-[0_4px_16px_rgba(124,154,146,0.12)] p-8">
        <div className="space-y-6">
          {/* Daily Message with Shuffle */}
          <div className="text-center space-y-3">
            <div className="inline-block p-3 bg-primary/10 rounded-full mb-2">
              <Heart className="w-8 h-8 text-primary fill-primary" strokeWidth={1.5} />
            </div>
            <p className="text-2xl font-nunito font-bold text-[#2D2D2D] italic leading-relaxed">
              "{groundingMessages[currentMessageIndex]}"
            </p>
            <button
              onClick={shuffleMessage}
              className="text-sm text-primary/70 hover:text-primary transition-colors flex items-center gap-1 mx-auto"
              data-testid="shuffle-message-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              New message
            </button>
          </div>

          {/* Quick Add Flare Button */}
          <div className="flex justify-center gap-3 flex-wrap">
            <Button
              onClick={() => setShowQuickFlare(true)}
              variant="outline"
              className="border-orange-300 text-orange-600 hover:bg-orange-50 rounded-full px-6 flex items-center gap-2"
              data-testid="quick-add-flare-btn"
            >
              <Flame className="w-4 h-4" />
              Quick Add Flare Entry
            </Button>
            <Button
              onClick={generateCareTeamSummary}
              variant="outline"
              className="border-accent text-accent hover:bg-accent/10 rounded-full px-6 flex items-center gap-2"
              disabled={careTeamLoading}
              data-testid="care-team-summary-btn"
            >
              <FileText className="w-4 h-4" />
              {careTeamLoading ? 'Generating...' : 'Care Team Summary PDF'}
            </Button>
          </div>

          {/* Status Selector */}
          <div className="space-y-3">
            <p className="text-center text-[#57534E] font-medium">
              Before you begin, take a breath. How does today feel?
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { value: 'calm', label: 'Calm', color: 'bg-primary', icon: '☀️' },
                { value: 'wobbly', label: 'Wobbly', color: 'bg-secondary', icon: '🌤️' },
                { value: 'intense', label: 'Intense', color: 'bg-accent', icon: '🌧️' }
              ].map(({ value, label, color, icon }) => (
                <button
                  key={value}
                  onClick={() => handleStatusChange(value)}
                  className={`px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 ${
                    selectedStatus === value
                      ? `${color} text-white shadow-lg scale-105`
                      : 'bg-stone-100 text-[#57534E] hover:bg-stone-200'
                  }`}
                  data-testid={`status-${value}`}
                >
                  <span className="mr-2">{icon}</span>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Normalizing Message */}
          <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-lg p-4 text-center">
            <p className="text-[#57534E] leading-relaxed">
              <span className="font-semibold text-primary">Remember:</span> Non-linear days are expected. 
              There's no "right" way to feel. Your emotions are valid, and this community is here for you.
            </p>
          </div>
        </div>
      </Card>

      {/* Grounding Technique (shows after status selection) */}
      {showTechnique && currentTechnique && (
        <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl border border-stone-200 p-6">
          <div className="space-y-4">
            <h3 className="text-xl font-nunito font-bold text-[#2D2D2D]">{currentTechnique.title}</h3>
            <p className="text-[#57534E]">{currentTechnique.description}</p>
            <ol className="space-y-2">
              {currentTechnique.steps.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{i + 1}</span>
                  <span className="text-[#57534E] pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </Card>
      )}

      {/* Journal Prompt for All Statuses */}
      {showJournal && selectedStatus && (
        <Card className={`bg-white rounded-xl border-2 p-6 ${
          selectedStatus === 'intense' ? 'border-accent/30' : 
          selectedStatus === 'calm' ? 'border-primary/30' : 'border-secondary/30'
        }`}>
          <div className="space-y-4">
            {selectedStatus === 'calm' && (
              <>
                <h3 className="text-xl font-nunito font-bold text-[#2D2D2D]">Capture the Good</h3>
                <p className="text-[#57534E]">
                  On calm days, it helps to notice what's going well. These moments matter:
                </p>
              </>
            )}
            {selectedStatus === 'wobbly' && (
              <>
                <h3 className="text-xl font-nunito font-bold text-[#2D2D2D]">You're In Between</h3>
                <p className="text-[#57534E]">
                  Wobbly days are okay. If you'd like, journal about what you're navigating:
                </p>
              </>
            )}
            {selectedStatus === 'intense' && (
              <>
                <h3 className="text-xl font-nunito font-bold text-[#2D2D2D]">You're having a tough day. That's okay.</h3>
                <p className="text-[#57534E]">
                  Writing can help. If you'd like, take a moment to journal what's making today feel intense:
                </p>
              </>
            )}
            <Textarea
              value={journalEntry}
              onChange={(e) => setJournalEntry(e.target.value)}
              placeholder={
                selectedStatus === 'calm' ? "What's contributing to today feeling calm? What can I remember for harder days?" :
                selectedStatus === 'wobbly' ? "What feels uncertain right now? What might help me feel more grounded?" :
                "What's making today feel overwhelming? What do you need right now?"
              }
              rows={6}
              className="w-full"
              data-testid="journal-textarea"
            />
            <Button
              onClick={saveJournalEntry}
              className={`${
                selectedStatus === 'calm' ? 'bg-primary text-white hover:bg-primary/90' :
                selectedStatus === 'wobbly' ? 'bg-secondary text-secondary-foreground hover:bg-secondary/80' :
                'bg-accent text-accent-foreground hover:bg-accent/90'
              } rounded-full px-6 py-2 font-semibold`}
              data-testid="save-journal-button"
            >
              Save Private Entry
            </Button>
            <p className="text-xs text-[#A8A29E] italic">This is private and just for you.</p>
          </div>
        </Card>
      )}

      {/* Weekly Reflection Prompt (Sundays) */}
      {showWeeklyReflection && (
        <Dialog open={showWeeklyReflection} onOpenChange={setShowWeeklyReflection}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-nunito font-bold">Weekly Reflection</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-[#57534E] leading-relaxed">
                Take a moment to reflect on the past week. What strategies, activities, or moments helped you cope?
              </p>
              <Textarea
                value={weeklyReflection}
                onChange={(e) => setWeeklyReflection(e.target.value)}
                placeholder="This week, what helped me cope was..."
                rows={8}
                className="w-full"
                data-testid="weekly-reflection-textarea"
              />
              <div className="flex gap-3">
                <Button
                  onClick={saveWeeklyReflection}
                  className="bg-primary text-white hover:bg-primary/90 rounded-full px-6 py-3 font-semibold"
                  data-testid="save-reflection-button"
                >
                  Save Reflection
                </Button>
                <Button
                  onClick={() => setShowWeeklyReflection(false)}
                  variant="ghost"
                  className="rounded-full px-6 py-3"
                >
                  Skip for Now
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Crisis Help Modal */}
      <Dialog open={showCrisisModal} onOpenChange={setShowCrisisModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-nunito font-bold text-red-600 flex items-center gap-2">
              <AlertCircle className="w-6 h-6" />
              Crisis Support
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Emergency Breathing */}
            <Card className="bg-red-50 border-2 border-red-200 p-6">
              <h3 className="font-nunito font-bold text-[#2D2D2D] mb-3 flex items-center gap-2">
                <Wind className="w-5 h-5 text-red-500" />
                Emergency Breathing - Do This Now
              </h3>
              <div className="space-y-2 text-[#57534E]">
                <p className="font-medium">Box Breathing (4-4-4-4):</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Breathe IN for 4 counts</li>
                  <li>HOLD for 4 counts</li>
                  <li>Breathe OUT for 4 counts</li>
                  <li>HOLD for 4 counts</li>
                  <li>Repeat 4 times</li>
                </ol>
              </div>
            </Card>

            {/* Bookmarked Tools */}
            {bookmarkedTools.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-nunito font-bold text-[#2D2D2D] flex items-center gap-2">
                  <Bookmark className="w-5 h-5 text-primary" />
                  Your Saved Tools
                </h3>
                <div className="grid gap-2">
                  {bookmarkedTools.slice(0, 6).map((bookmark) => (
                    <button
                      key={bookmark.id}
                      onClick={() => {
                        setShowCrisisModal(false);
                        navigate(bookmark.tool_type === 'parent-tool' ? '/parent-regulation' : '/child-regulation');
                      }}
                      className="flex items-center justify-between p-3 bg-white border border-stone-200 rounded-lg hover:bg-primary/5 hover:border-primary/30 transition-colors text-left"
                      data-testid={`crisis-bookmark-${bookmark.tool_id}`}
                    >
                      <span className="font-medium text-[#2D2D2D]">{bookmark.tool_title}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        bookmark.tool_type === 'parent-tool' ? 'bg-primary/10 text-primary' : 'bg-secondary text-secondary-foreground'
                      }`}>
                        {bookmark.tool_type === 'parent-tool' ? 'For You' : 'For Child'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Links */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => {
                  setShowCrisisModal(false);
                  navigate('/parent-regulation');
                }}
                className="bg-primary text-white hover:bg-primary/90 rounded-lg py-6 flex flex-col items-center gap-2"
                data-testid="crisis-parent-tools"
              >
                <Headphones className="w-6 h-6" />
                <span>Parent Tools</span>
              </Button>
              <Button
                onClick={() => {
                  setShowCrisisModal(false);
                  navigate('/child-regulation');
                }}
                className="bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-lg py-6 flex flex-col items-center gap-2"
                data-testid="crisis-child-tools"
              >
                <Smile className="w-6 h-6" />
                <span>Child Tools</span>
              </Button>
            </div>

            {/* Crisis Resources */}
            <Card className="bg-stone-50 border border-stone-200 p-4">
              <h3 className="font-nunito font-bold text-[#2D2D2D] mb-3 flex items-center gap-2">
                <Phone className="w-5 h-5 text-[#57534E]" />
                Crisis Hotlines
              </h3>
              <div className="space-y-2 text-sm text-[#57534E]">
                <p><strong>988 Suicide & Crisis Lifeline:</strong> Call or text 988</p>
                <p><strong>Crisis Text Line:</strong> Text HOME to 741741</p>
                <p><strong>PANDAS Network Crisis:</strong> pandasnetwork.org/resources</p>
              </div>
            </Card>

            {/* Reminder */}
            <p className="text-center text-[#A8A29E] text-sm italic">
              This moment will pass. You've survived 100% of your hard days so far.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Flare Journal Prompt Modal */}
      <Dialog open={showFlarePrompt} onOpenChange={setShowFlarePrompt}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-nunito">
              <Flame className="w-6 h-6 text-orange-500" />
              Document This Intense Moment?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#57534E] leading-relaxed">
              Intense days are hard. Documenting what's happening can help you:
            </p>
            <ul className="space-y-2 text-[#57534E]">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-sm flex-shrink-0 mt-0.5">✓</span>
                <span>Identify patterns and triggers over time</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-sm flex-shrink-0 mt-0.5">✓</span>
                <span>Remember what helped (or didn't)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-sm flex-shrink-0 mt-0.5">✓</span>
                <span>Share insights with your child's care team</span>
              </li>
            </ul>
            <p className="text-sm text-[#A8A29E] italic">
              No pressure — you can also skip this and come back later.
            </p>
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => {
                  setShowFlarePrompt(false);
                  navigate('/flare-journal');
                }}
                className="flex-1 bg-orange-500 text-white hover:bg-orange-600 rounded-full flex items-center justify-center gap-2"
                data-testid="flare-journal-go-btn"
              >
                <Flame className="w-4 h-4" />
                Open Flare Journal
              </Button>
              <Button
                onClick={() => setShowFlarePrompt(false)}
                variant="outline"
                className="rounded-full"
                data-testid="flare-journal-skip-btn"
              >
                Skip for Now
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Add Flare Modal */}
      <Dialog open={showQuickFlare} onOpenChange={setShowQuickFlare}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-nunito">
              <Flame className="w-6 h-6 text-orange-500" />
              Quick Flare Entry
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <p className="text-sm text-[#57534E]">
              Capture this moment quickly. You can add more details later in the full Flare Journal.
            </p>

            {/* Severity */}
            <div>
              <label className="text-sm font-medium text-[#2D2D2D]">Severity: {quickFlareData.severity}/10</label>
              <input
                type="range"
                min="1"
                max="10"
                value={quickFlareData.severity}
                onChange={(e) => setQuickFlareData(prev => ({ ...prev, severity: parseInt(e.target.value) }))}
                className="w-full mt-2"
              />
              <div className="flex justify-between text-xs text-[#A8A29E]">
                <span>Mild</span>
                <span>Moderate</span>
                <span>Severe</span>
              </div>
            </div>

            {/* Quick Symptoms */}
            <div>
              <label className="text-sm font-medium text-[#2D2D2D]">Symptoms</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {commonSymptoms.map((symptom) => (
                  <button
                    key={symptom}
                    type="button"
                    onClick={() => toggleQuickSymptom(symptom)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                      quickFlareData.symptoms.includes(symptom)
                        ? 'bg-red-500 text-white'
                        : 'bg-stone-100 text-[#57534E] hover:bg-stone-200'
                    }`}
                  >
                    {symptom}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Behaviors */}
            <div>
              <label className="text-sm font-medium text-[#2D2D2D]">Behaviors</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {commonBehaviors.map((behavior) => (
                  <button
                    key={behavior}
                    type="button"
                    onClick={() => toggleQuickBehavior(behavior)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                      quickFlareData.behaviors.includes(behavior)
                        ? 'bg-orange-500 text-white'
                        : 'bg-stone-100 text-[#57534E] hover:bg-stone-200'
                    }`}
                  >
                    {behavior}
                  </button>
                ))}
              </div>
            </div>

            {/* What's helping */}
            <div>
              <label className="text-sm font-medium text-[#2D2D2D]">What's helping right now? (optional)</label>
              <Textarea
                value={quickFlareData.what_helped}
                onChange={(e) => setQuickFlareData(prev => ({ ...prev, what_helped: e.target.value }))}
                placeholder="Deep pressure, quiet room, cold water..."
                rows={2}
                className="mt-1"
              />
            </div>

            {/* Quick notes */}
            <div>
              <label className="text-sm font-medium text-[#2D2D2D]">Quick note (optional)</label>
              <Textarea
                value={quickFlareData.notes}
                onChange={(e) => setQuickFlareData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Anything to remember about this moment..."
                rows={2}
                className="mt-1"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={saveQuickFlare}
                disabled={quickFlareSaving}
                className="flex-1 bg-orange-500 text-white hover:bg-orange-600 rounded-full"
                data-testid="quick-flare-save-btn"
              >
                {quickFlareSaving ? 'Saving...' : 'Save Entry'}
              </Button>
              <Button
                onClick={() => {
                  setShowQuickFlare(false);
                  navigate('/flare-journal');
                }}
                variant="outline"
                className="rounded-full"
                data-testid="quick-flare-full-btn"
              >
                Full Journal
              </Button>
            </div>

            <p className="text-xs text-[#A8A29E] text-center italic">
              Entry will be saved to your Flare Journal
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Floating Crisis Button */}
      <button
        onClick={() => setShowCrisisModal(true)}
        className="fixed bottom-6 right-6 bg-red-500 hover:bg-red-600 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300 z-50 flex items-center gap-2"
        data-testid="crisis-button"
      >
        <AlertCircle className="w-6 h-6" />
        <span className="font-semibold pr-1">Crisis Help</span>
      </button>

      {/* Quick Actions */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-nunito font-bold text-[#2D2D2D]">Quick Actions</h2>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate('/parent-regulation')}
              variant="outline"
              size="sm"
              className="text-xs rounded-full border-primary/30 text-primary hover:bg-primary/10"
              data-testid="dashboard-parent-reg-btn"
            >
              <Headphones className="w-3.5 h-3.5 mr-1" />
              Parent Tools
            </Button>
            <Button
              onClick={() => navigate('/child-regulation')}
              variant="outline"
              size="sm"
              className="text-xs rounded-full border-primary/30 text-primary hover:bg-primary/10"
              data-testid="dashboard-child-reg-btn"
            >
              <Smile className="w-3.5 h-3.5 mr-1" />
              Child Tools
            </Button>
            <Button
              onClick={() => navigate('/timeline')}
              variant="outline"
              size="sm"
              className="text-xs rounded-full border-primary/30 text-primary hover:bg-primary/10"
              data-testid="dashboard-timeline-btn"
            >
              <TrendingUp className="w-3.5 h-3.5 mr-1" />
              Timeline & Insights
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-9 gap-3">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <Card
                key={index}
                className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-4 hover:shadow-[0_8px_24px_rgba(124,154,146,0.15)] hover:border-primary/30 transition-all duration-300 cursor-pointer hover:-translate-y-1 aspect-square flex flex-col items-center justify-center text-center"
                onClick={() => navigate(action.path)}
                data-testid={action.testId}
              >
                <Icon className={`w-10 h-10 ${action.color} mb-2`} strokeWidth={1.5} />
                <h3 className="text-sm font-nunito font-semibold text-[#2D2D2D] leading-tight">
                  {action.title}
                </h3>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Recent Symptoms */}
      {recentSymptoms.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-nunito font-bold text-[#2D2D2D]">Recent Symptom Logs</h2>
            <Button
              onClick={() => navigate('/tracker')}
              className="bg-primary text-white hover:bg-primary/90 rounded-full px-6 py-2 font-semibold"
              data-testid="view-all-symptoms-button"
            >
              View All
            </Button>
          </div>
          <div className="space-y-4">
            {recentSymptoms.map((entry) => (
              <Card key={entry.id} className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-nunito font-bold text-[#2D2D2D]">{entry.child_name}</h3>
                      <span className="text-sm text-[#A8A29E]">
                        {formatLocalDate(entry.date)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {entry.symptoms.map((symptom, i) => (
                        <span key={i} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                          {symptom}
                        </span>
                      ))}
                    </div>
                    {entry.notes && (
                      <p className="text-[#57534E] text-sm">{entry.notes}</p>
                    )}
                  </div>
                  <div className="ml-4">
                    <div className={`px-4 py-2 rounded-lg font-bold ${
                      entry.severity >= 7 ? 'bg-accent/20 text-accent' :
                      entry.severity >= 4 ? 'bg-secondary text-secondary-foreground' :
                      'bg-primary/10 text-primary'
                    }`}>
                      {entry.severity}/10
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Subtle Insights Section (Bottom of Dashboard) */}
      <div className="pt-8 border-t border-stone-200">
        <button
          onClick={() => setShowInsights(!showInsights)}
          className="flex items-center gap-2 text-[#A8A29E] hover:text-primary transition-colors text-sm"
          data-testid="toggle-insights"
        >
          {showInsights ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          <span>{showInsights ? 'Hide' : 'View'} Insights & Patterns</span>
        </button>

        {showInsights && checkInHistory.length > 0 && (
          <div className="mt-6 space-y-6">
            {/* Mood Pattern Overview */}
            <Card className="bg-stone-50 rounded-xl border border-stone-200 p-6">
              <h3 className="text-lg font-nunito font-semibold text-[#2D2D2D] mb-4">
                Last 30 Days - Mood Patterns
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 min-w-[100px]">
                    <span>☀️</span>
                    <span className="text-sm text-[#57534E]">Calm</span>
                  </div>
                  <div className="flex-1 bg-white rounded-full h-6 overflow-hidden">
                    <div
                      className="bg-primary h-full flex items-center justify-end pr-2 text-white text-xs font-semibold"
                      style={{ width: `${getMoodStats().calm}%` }}
                    >
                      {getMoodStats().calm > 10 && `${getMoodStats().calm}%`}
                    </div>
                  </div>
                  {getMoodStats().calm <= 10 && <span className="text-xs text-[#57534E] min-w-[40px]">{getMoodStats().calm}%</span>}
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 min-w-[100px]">
                    <span>🌤️</span>
                    <span className="text-sm text-[#57534E]">Wobbly</span>
                  </div>
                  <div className="flex-1 bg-white rounded-full h-6 overflow-hidden">
                    <div
                      className="bg-secondary h-full flex items-center justify-end pr-2 text-secondary-foreground text-xs font-semibold"
                      style={{ width: `${getMoodStats().wobbly}%` }}
                    >
                      {getMoodStats().wobbly > 10 && `${getMoodStats().wobbly}%`}
                    </div>
                  </div>
                  {getMoodStats().wobbly <= 10 && <span className="text-xs text-[#57534E] min-w-[40px]">{getMoodStats().wobbly}%</span>}
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 min-w-[100px]">
                    <span>🌧️</span>
                    <span className="text-sm text-[#57534E]">Intense</span>
                  </div>
                  <div className="flex-1 bg-white rounded-full h-6 overflow-hidden">
                    <div
                      className="bg-accent h-full flex items-center justify-end pr-2 text-accent-foreground text-xs font-semibold"
                      style={{ width: `${getMoodStats().intense}%` }}
                    >
                      {getMoodStats().intense > 10 && `${getMoodStats().intense}%`}
                    </div>
                  </div>
                  {getMoodStats().intense <= 10 && <span className="text-xs text-[#57534E] min-w-[40px]">{getMoodStats().intense}%</span>}
                </div>
              </div>

              <p className="text-xs text-[#A8A29E] mt-4 italic">
                Based on {checkInHistory.length} check-ins over the last 30 days
              </p>
            </Card>

            {/* Recent Check-in History */}
            <Card className="bg-stone-50 rounded-xl border border-stone-200 p-6">
              <h3 className="text-lg font-nunito font-semibold text-[#2D2D2D] mb-4">
                Recent Check-ins
              </h3>
              <div className="space-y-2">
                {checkInHistory.slice(0, 7).map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between py-2 border-b border-stone-200 last:border-0">
                    <span className="text-sm text-[#57534E]">{formatLocalDate(entry.date, 'en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                    <span className="px-3 py-1 rounded-full text-sm font-medium">
                      {entry.status === 'calm' && <span className="bg-primary/20 text-primary">☀️ Calm</span>}
                      {entry.status === 'wobbly' && <span className="bg-secondary text-secondary-foreground">🌤️ Wobbly</span>}
                      {entry.status === 'intense' && <span className="bg-accent/20 text-accent">🌧️ Intense</span>}
                    </span>
                  </div>
                ))}
              </div>
            </Card>

            <p className="text-xs text-[#A8A29E] text-center italic">
              This data is here if you want it, but there's no pressure to track perfectly. 
              You're doing great.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
